import { useRef, useState, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import './FormRegisterUser.scss';
import { languageElements } from './FormRegisterUser-languageElements';
import LanguageElementsHandler from '../../../repository/languageElementsHandler/LanguageElementsHandler';
import SelFP from '../../../repository/SelForm/SelFP';
import SelFieldGroup from '../../../repository/SelForm/SelFieldGroup/SelFieldGroup';
import FiveSecondsAlert from '../../alerts/fiveSecondsAlert/FiveSecondsAlert';
import BlockScreen from '../../alerts/blockScreen/BlockScreen';
import SimpleModal from '../../modals/simpleModal/SimpleModal';
import PDFViewer from '../../PDFViewer/PDFViewer';
import useMain from '../../../common/MainContext';

export default function FormRegisterUser(props) {
    const { language } = useMain();
    const languageElementsHandler = new LanguageElementsHandler(languageElements, language);

    const fields = [{
        reference: useRef(),
        name: 'Name',
        labelText: languageElementsHandler.get(`field-Name`),
        type: 'text',
        initialValue: '',
        validators: ['required',]
    },
    {
        reference: useRef(),
        name: 'Email',
        labelText: languageElementsHandler.get(`field-Email`),
        type: 'text',
        initialValue: '',
        validators: ['required', 'validEmail',],
    },
    {
        reference: useRef(),
        name: 'TermsAndConditionsAccepted',
        labelText: <>
            {languageElementsHandler.get('field-TermsAndConditionsAccepted-before')}
            <a href='#' onClick={(e) => {
                e.preventDefault();
                showTermsAndConditions();
            }}> {languageElementsHandler.get('field-TermsAndConditionsAccepted-link')} </a>
            {languageElementsHandler.get('field-TermsAndConditionsAccepted-after')}
        </>,
        type: 'checkbox',
        validators: ['mustAccept',],
    },
    {
        reference: useRef(),
        name: 'Password',
        labelText: languageElementsHandler.get(`field-Password`),
        type: 'password',
        initialValue: '',
        validators: [
            'required',
            (field, value, fpStates) => {
                const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
                return (!regex.test(fpStates.fieldValues.Password)) ? languageElementsHandler.get('validate-password-not-complex-enough') : ''
            }
        ],
    },
    {
        reference: useRef(),
        name: 'PasswordAgain',
        labelText: languageElementsHandler.get(`field-PasswordAgain`),
        type: 'password',
        initialValue: '',
        validators: [
            'required',
            (field, value, fpStates) => {
                return (value !== fpStates.fieldValues.Password) ? languageElementsHandler.get('validate-password-and-passwordAgain-not-match') : ''
            }
        ],
    },
    ]

    const fp = new SelFP({ language, languageElements, fields, });

    const [dataState, setDataState] = useState('LOADING');
    const [fpStates, setFpStates] = useState(fp.initStates());
    const [stateOfBlockScreen, setStateOfBlockScreen] = useState({
        show: false,
    });
    const [stateOfFiveSecondsAlert, setStateOfFiveSecondsAlert] = useState({
        show: false,
        message: {
            title: '',
            body: '',
        }
    });
    const modalBaseState = {
        show: false,
    };
    const [modalShow, setModalShow] = useState(modalBaseState);

    useEffect(() => {
        if (!props.regLoginData) {
            return;
        }

        axios.get(`${process.env.REACT_APP_API_BASE_URL}/registration`, {
            headers: {
                'x-token': props.regLoginData.getToken(),
            }
        }).then(result => {
            fp.setValues(fpStates, setFpStates, result.data);
            setDataState('LOADED');
        }).catch(error => {
            console.error(error);
        });
    }, [props.regLoginData])

    function handleInputBlur(e) {
        fp.handleInputBlur(e, fpStates, setFpStates);
    }

    function handleInputChange(e) {
        fp.handleInputChange(e, fpStates, setFpStates);
    }

    function showTermsAndConditions() {
        setModalShow({
            show: true,
            fullscreen: true,
            closeButton: true,
            body: <PDFViewer fileURL={`./legalTerms/TermsAndConditions_${language}.pdf`} />
        })
    }

    async function save() {
        if (!fp.isGroupValid(fpStates, setFpStates)) {
            return;
        }

        setStateOfBlockScreen({ show: true })

        const data = {
            ...fp.stringifyValues(fpStates),
            Status: 'ACTIVE',
        }

        axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/registration`,
            data,
            {
                headers: {
                    "x-token": props.regLoginData.getToken(),
                },
            }
        ).then(result => {
            setStateOfFiveSecondsAlert({
                show: true,
                variant: 'primary',
                interval: 1500,
                className: 'text-center',
                message: {
                    title: languageElementsHandler.get('MESSAGE_DATA_SUCCESSFULLY_SAVED-title')
                },
                callback: () => props.callback({ result: 'SAVED' }),
            });

            return;
        }).catch(err => {
            switch (err.response?.data?.message || '') {
                case 'EMAIL ALREADY EXISTS':
                    setStateOfFiveSecondsAlert({
                        show: true,
                        variant: 'danger',
                        className: 'text-center',
                        message: {
                            title: languageElementsHandler.get('MESSAGE_USER_ALREADY_EXISTS-title')
                        }
                    });
                    break;

                default:
                    console.error(err);
                    break;
            }
        }).finally(() => {
            setStateOfBlockScreen({ show: false })
        });
    }

    return (
        <div className='form-register-user'>
            <BlockScreen
                show={stateOfBlockScreen.show}
                variant={stateOfBlockScreen.variant}
                className={stateOfBlockScreen.className}
            />
            <FiveSecondsAlert
                show={stateOfFiveSecondsAlert.show}
                message={stateOfFiveSecondsAlert.message}
                variant={stateOfFiveSecondsAlert.variant}
                interval={stateOfFiveSecondsAlert.interval}
                className={stateOfFiveSecondsAlert.className}
                callback={() => {
                    setStateOfFiveSecondsAlert({ show: false });
                    if (stateOfFiveSecondsAlert.callback) {
                        stateOfFiveSecondsAlert.callback();
                    }
                }}
            />
            <SimpleModal
                show={modalShow.show}
                closeButton={modalShow.closeButton}
                title={modalShow.title}
                body={modalShow.body}
                fullscreen={modalShow.fullscreen}
                buttons={modalShow.buttons || []}
                callback={(buttonId, params) => {
                    if (modalShow.callback) {
                        modalShow.callback(buttonId, params);
                        return;
                    }
                    setModalShow(false);
                }}
            />

            <h2>{languageElementsHandler.get('form-title')}</h2>

            {
                (dataState === 'LOADING') &&
                <div className='spinner-centered'>
                    <Spinner variant='danger' />
                </div>
            }

            {
                (dataState === 'LOADED') &&
                <form
                    noValidate={true}
                    className={`needs-validation ${fpStates.formWasValidated ? 'was-validated' : ''}`}
                >
                    <div className='form-register-user-data'>
                        <SelFieldGroup
                            languageElements={languageElements}
                            fields={fields}
                            fieldNames={['Name', 'Email', 'TermsAndConditionsAccepted',]}
                            fpStates={fpStates}
                            handleInputBlur={handleInputBlur}
                            handleInputChange={handleInputChange}
                        />
                    </div>

                    <div className='form-register-user-terms-and-conditions'>
                        <SelFieldGroup
                            languageElements={languageElements}
                            fields={fields}
                            fieldNames={['Password', 'PasswordAgain',]}
                            fpStates={fpStates}
                            handleInputBlur={handleInputBlur}
                            handleInputChange={handleInputChange}
                        />
                    </div>

                    <div className="btn-area">
                        <Button onClick={save}>
                            {languageElementsHandler.get(`btn-submit`)}
                        </Button>
                    </div>
                </form>
            }
        </div>
    )
}
